import { useFormik } from "formik"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { array, number, object, string } from "yup"

import { ProjectFormPageProps } from "components/ProjectForm"
import { EProjectFormPages } from "components/ProjectForm/config"
import useIsCpoDeal, { CPO_DEFAULT_OCPP_URL, CPO_DEFAULT_PORT_NUMBER } from "components/ProjectForm/cpoDefaults"
import { useTemplateUIRules } from "components/ProjectForm/context/TemplateUIRulesContext"
import { setInfraForm } from "components/ProjectForm/ProjectFormSlice"
import { EFileTypes } from "enums/fileTypeEnums"
import { useGetInfraSowsQuery, useGetProductsQuery } from "pages/Common/commonService"
import { DropdownObj, Product } from "pages/Common/commonTypes"
import { SubmitFileInfo, useSubmitFinalUploadsMutation } from "pages/Common/fileUploadService"
import {
    IInfraDetailsPageData,
    IProjectCharger,
    IProjectFormDocument,
    useGetFormChargersQuery,
    useGetSurveysForProjectQuery,
    useLazyGetDispatchedChargerProjectsPaginatedQuery,
    useLazyGetFormChargersQuery,
    useLazyGetProjectFormDetailsByStepQuery,
    useUpdateTicketPageDataMutation,
} from "pages/Projects/ProjectServices"
import { useLazyGetDemolitionSiteMaterialsQuery } from "pages/Operations/features/Demolition/DemolitionService"
import { TStore } from "store/Store"
import { ChargerTableRow, createEmptyChargerRow } from "./ChargerTable"

export enum EInfraFields {
    INFRA_SCOPE_OF_WORK = "sow_group_ids",
    SURVEY_TICKET_ID = "survey_id",
    IS_MATERIAL_NEEDED = "is_material_needed",
    OCPP_URL = "ocpp_url",
    PORT_NUMBER = "port_number",
    ARE_CHARGERS_SUPPLIED_BY_STATIQ = "supplied_by_statiq",
    SELECT_PROJECT = "select_project",
    SELECT_CHARGERS_TO_ADD = "select_chargers_to_add",
    IS_EVA_REQUIRED = "eva_required",
    EV_CONNECTION_OWNED_BY = "connection_owned_by",
    REMARKS = "infra_remarks",
}

export type InfraFormValues = {
    [EInfraFields.INFRA_SCOPE_OF_WORK]: DropdownObj[]
    [EInfraFields.SURVEY_TICKET_ID]: DropdownObj | null
    [EInfraFields.IS_MATERIAL_NEEDED]: DropdownObj | null
    [EInfraFields.OCPP_URL]: string
    [EInfraFields.PORT_NUMBER]: string
    [EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]: DropdownObj | null
    [EInfraFields.SELECT_PROJECT]: DropdownObj | null
    [EInfraFields.SELECT_CHARGERS_TO_ADD]: DropdownObj[]
    [EInfraFields.IS_EVA_REQUIRED]: DropdownObj | null
    [EInfraFields.EV_CONNECTION_OWNED_BY]: { id: string; title: string } | null
    [EInfraFields.REMARKS]: string
}

const initialValues: InfraFormValues = {
    [EInfraFields.INFRA_SCOPE_OF_WORK]: [],
    [EInfraFields.SURVEY_TICKET_ID]: null,
    [EInfraFields.IS_MATERIAL_NEEDED]: {
        id: 2,
        title: "No",
    },
    [EInfraFields.OCPP_URL]: "",
    [EInfraFields.PORT_NUMBER]: "",
    [EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]: {
        id: 1,
        title: "Yes",
    },
    [EInfraFields.SELECT_PROJECT]: null,
    [EInfraFields.SELECT_CHARGERS_TO_ADD]: [],
    [EInfraFields.IS_EVA_REQUIRED]: {
        id: 2,
        title: "No",
    },
    [EInfraFields.EV_CONNECTION_OWNED_BY]: {
        id: "Statiq",
        title: "Statiq",
    },
    [EInfraFields.REMARKS]: "",
}

const dropdownSchema = object({
    id: number().required(),
    title: string().required(),
})

const stringDropdownSchema = object({
    id: string().required(),
    title: string().required(),
})

const optionalDropdownSchema = dropdownSchema.nullable().default(null)
const requiredDropdownSchema = dropdownSchema.required("This field is required")

// Helper to check if Commissioning scope is selected
const hasCommissioningScope = (scopes: DropdownObj[]) => {
    return scopes.some(scope => scope.title?.toLowerCase().includes("commissioning"))
}

// Helper to check if Installation or Electrical Work scope is selected
const hasInstallationOrElectricalScope = (scopes: DropdownObj[]) => {
    return scopes.some(
        scope =>
            scope.title?.toLowerCase().includes("installation") || scope.title?.toLowerCase().includes("electrical")
    )
}

const validationSchema = object({
    [EInfraFields.INFRA_SCOPE_OF_WORK]: array()
        .of(dropdownSchema)
        .min(1, "Infra scope of work is required")
        .required("Infra scope of work is required"),
    [EInfraFields.SURVEY_TICKET_ID]: optionalDropdownSchema,
    [EInfraFields.IS_MATERIAL_NEEDED]: requiredDropdownSchema,
    [EInfraFields.OCPP_URL]: string()
        .trim()
        .default("")
        .when(EInfraFields.INFRA_SCOPE_OF_WORK, {
            is: (scopes: DropdownObj[]) => hasCommissioningScope(scopes),
            then: schema => schema.required("OCPP URL is required"),
            otherwise: schema => schema,
        }),
    [EInfraFields.PORT_NUMBER]: string()
        .trim()
        .transform(value => value ?? "")
        .default(""),
    [EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]: requiredDropdownSchema,
    [EInfraFields.SELECT_PROJECT]: optionalDropdownSchema,
    [EInfraFields.SELECT_CHARGERS_TO_ADD]: array().of(dropdownSchema).default([]),
    [EInfraFields.IS_EVA_REQUIRED]: optionalDropdownSchema,
    [EInfraFields.EV_CONNECTION_OWNED_BY]: stringDropdownSchema
        .nullable()
        .default(null)
        .when([EInfraFields.IS_EVA_REQUIRED], {
            is: (isEvaRequired: DropdownObj | null) => isEvaRequired?.title === "Yes",
            then: schema => schema.required("EV Connection Owned By is required"),
            otherwise: schema => schema.nullable(),
        }),
    [EInfraFields.REMARKS]: string()
        .trim()
        .transform(value => value ?? "")
        .default(""),
})

/**
 * Chargers at these operational statuses can't be picked in "Select chargers to add".
 * Only 198 (Active) is locked; 203 (Open) was removed so Open chargers can be added.
 */
const LOCKED_CHARGER_STATUS_IDS = [198]
const isChargerDisabled = (chargerStatusId: number) => LOCKED_CHARGER_STATUS_IDS.includes(chargerStatusId)

const useInfra = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const { infraRules } = useTemplateUIRules()
    const yesNoOptions = [
        {
            id: 1,
            title: "Yes",
        },
        {
            id: 2,
            title: "No",
        },
    ]

    const evConnectionOwnedByOptions = [
        {
            id: "Statiq",
            title: "Statiq",
        },
        {
            id: "Host",
            title: "Host",
        },
    ]
    const { project_id, infra_form, chargers_and_materials_form, account_n_sites_form_values } = useSelector(
        (store: TStore) => store.ProjectForm
    )
    const isCpoDeal = useIsCpoDeal()
    const hasAppliedDefaultScope = useRef(false)
    const hasPrefilled = useRef(false)
    const hasPreselectedChargers = useRef<number | null>(null)
    const [showMore, setShowMore] = useState(false)
    const isInitiallyStatiqSupplied = infra_form?.[EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]?.id === 1
    const [statiqChargerRows, setStatiqChargerRows] = useState<ChargerTableRow[]>(
        isInitiallyStatiqSupplied ? infra_form?.charger_details || [] : []
    )
    const [nonStatiqChargerRows, setNonStatiqChargerRows] = useState<ChargerTableRow[]>(
        !isInitiallyStatiqSupplied ? infra_form?.charger_details || [] : []
    )
    const [submitFinalUploads, { isLoading: isSubmittingFinalUploads }] = useSubmitFinalUploadsMutation()
    const [getProjectFormDetailsByStep] = useLazyGetProjectFormDetailsByStepQuery()

    const [existingInfraDocuments, setExistingInfraDocuments] = useState<IProjectFormDocument[]>([])
    const [addChargersFrom, setAddChargersFrom] = useState<DropdownObj | null>({ id: 1, title: "Projects" })
    const pendingFileInfosRef = useRef<SubmitFileInfo[]>([])

    const FILE_TYPES = {
        INFRA_DOCUMENT: EFileTypes.INFRA_DOCUMENT,
    }

    const handleFileUpload = (fileInfos: SubmitFileInfo[], previousFileInfos?: SubmitFileInfo[]) => {
        const prevDocIds = new Set(previousFileInfos?.map(f => f.doc_id))
        pendingFileInfosRef.current = [
            ...pendingFileInfosRef.current.filter(f => !prevDocIds.has(f.doc_id)),
            ...fileInfos,
        ]
    }

    const submitPendingFiles = async () => {
        if (pendingFileInfosRef.current.length === 0 || !project_id) return
        try {
            await submitFinalUploads({
                ticket_id: project_id,
                file_infos: pendingFileInfosRef.current,
            }).unwrap()
            pendingFileInfosRef.current = []
        } catch (error) {
            console.error("Error submitting final uploads:", error)
        }
    }

    const { data: surveyList } = useGetSurveysForProjectQuery()
    const { data: chargerProducts } = useGetProductsQuery({ is_charger: 1 })
    const [getDispatchedChargerProjects] = useLazyGetDispatchedChargerProjectsPaginatedQuery()
    const [
        fetchSelectedProjectChargers,
        { data: selectedProjectChargers, isFetching: isSelectedProjectChargersFetching },
    ] = useLazyGetFormChargersQuery()
    const { data: prefilledChargersFromMaterials } = useGetFormChargersQuery(project_id!, {
        skip: !project_id,
    })

    const isSiteSelected = addChargersFrom?.title === "Site"

    const [
        fetchSiteChargers,
        { data: siteChargers, isFetching: isSiteChargersFetching },
    ] = useLazyGetDemolitionSiteMaterialsQuery()

    const destinationSiteOption = account_n_sites_form_values?.destinationSite
        ? {
              id: account_n_sites_form_values.destinationSite.id,
              title: account_n_sites_form_values.destinationSite.name,
          }
        : null

    const selectedSiteChargerOptions = useMemo(() => {
        if (!siteChargers?.chargers) return []
        return siteChargers.chargers.map((charger, index) => ({
            id: charger.plp_id,
            title: charger.sku_code
                ? `${charger.sku_code}${charger.serial_number ? ` - ${charger.serial_number}` : ""}${charger.operational_status ? ` - ${charger.operational_status}` : ""}`
                : charger.display_name || `Charger ${index + 1}`,
            charger: {
                sku: charger.sku_code,
                display_name: charger.display_name,
                cin: charger.serial_number || "",
                ocpp_id: "",
                remarks: "",
                project_linked_product_id: charger.plp_id,
                project_id: charger.project_id ?? null,
                operational_parent_status_label: charger.operational_status ?? null,
                operational_status_label: charger.charger_sub_status ?? null,
                operational_status_color: null,
                operational_status_text_color: null
            } as unknown as IProjectCharger,
        }))
    }, [siteChargers])
    const [updateTicketPageData, { isLoading: isUpdatingPageData }] = useUpdateTicketPageDataMutation()
    const { data: infraSows, isFetching: infraSowsIsFetching } = useGetInfraSowsQuery()

    const infraSOWOptions =
        infraSows?.map(i => ({
            id: i.id,
            title: i.name,
        })) || []

    const surveyOptions = useMemo(() => {
        return (
            surveyList?.map((item: any) => ({
                id: item.id,
                title: item.name,
            })) || []
        )
    }, [surveyList])

    const formik = useFormik<InfraFormValues>({
        initialValues: infra_form ? { ...infra_form } : initialValues,
        validationSchema,
        validateOnChange: false,
        onSubmit: handleSubmit,
    })

    const hasMaterialDetailsPage = Boolean(props.pages?.includes(EProjectFormPages.MATERIAL_DETAILS))
    const hasChargersInDetailsPage = (chargers_and_materials_form?.chargers?.length || 0) > 0
    const shouldEnforceStatiqSupplyDefault = hasMaterialDetailsPage && hasChargersInDetailsPage

    const isStatiqSupplied = formik.values[EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]?.id === 1
    const chargerTableRows = isStatiqSupplied ? statiqChargerRows : nonStatiqChargerRows
    const hasChargerData = statiqChargerRows.length > 0

    const showAddChargersFromProjects = isStatiqSupplied

    const addChargersFromOptions = [
        { id: 1, title: "Projects" },
        { id: 2, title: "Site" },
    ]

    const statiqSupplyOptions = useMemo(() => {
        if (!shouldEnforceStatiqSupplyDefault) return yesNoOptions

        return yesNoOptions.map(option => (option.id === 2 ? { ...option, disabled: true } : option))
    }, [shouldEnforceStatiqSupplyDefault, yesNoOptions])

    useEffect(() => {
        if (shouldEnforceStatiqSupplyDefault && formik.values[EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]?.id !== 1) {
            formik.setFieldValue(EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ, { id: 1, title: "Yes" })
        }
    }, [shouldEnforceStatiqSupplyDefault, formik.values[EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]])

    const fetchAndPrefillInfraData = useCallback(async () => {
        if (!props.isEditMode || !props.projectId || hasPrefilled.current) return
        if (infraSOWOptions.length === 0) return

        try {
            const response = await getProjectFormDetailsByStep({
                project_id: props.projectId,
                page_title: EProjectFormPages.INFRA_DETAILS,
            }).unwrap()

            const documents = (response?.documents || []) as IProjectFormDocument[]
            const pageFormData = response?.pages?.[EProjectFormPages.INFRA_DETAILS]
            if (!pageFormData) return

            hasPrefilled.current = true
            const data = pageFormData.page_data || pageFormData

            const infraDocs = documents.filter(doc => doc.document_type === FILE_TYPES.INFRA_DOCUMENT)
            setExistingInfraDocuments(infraDocs)

            if (data.sow_group_ids?.length > 0) {
                const selectedScopes = infraSOWOptions.filter((opt: any) => data.sow_group_ids.includes(opt.id))
                if (selectedScopes.length > 0) {
                    formik.setFieldValue(EInfraFields.INFRA_SCOPE_OF_WORK, selectedScopes)
                    hasAppliedDefaultScope.current = true
                }
            }
            if (data.cms_url || data.ocpp_url)
                formik.setFieldValue(EInfraFields.OCPP_URL, data.cms_url || data.ocpp_url)
            if (data.port_number != null) formik.setFieldValue(EInfraFields.PORT_NUMBER, String(data.port_number))
            if (data.eva_required != null) {
                formik.setFieldValue(
                    EInfraFields.IS_EVA_REQUIRED,
                    data.eva_required ? { id: 1, title: "Yes" } : { id: 2, title: "No" }
                )
            }
            if (data.connection_owned_by != null) {
                const option = [
                    { id: 1, title: "Statiq" },
                    { id: 2, title: "Host" },
                ].find(o => o.id === data.connection_owned_by)
                if (option) formik.setFieldValue(EInfraFields.EV_CONNECTION_OWNED_BY, option)
            }
            if (data.survey_id) {
                const selectedSurvey = surveyOptions.find(survey => survey.id === data.survey_id)
                if (selectedSurvey) {
                    formik.setFieldValue(EInfraFields.SURVEY_TICKET_ID, selectedSurvey)
                }
            }
            if (data.infra_remarks) formik.setFieldValue(EInfraFields.REMARKS, data.infra_remarks)
            if (data.supplied_by_statiq != null) {
                formik.setFieldValue(
                    EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ,
                    data.supplied_by_statiq === 1 ? { id: 1, title: "Yes" } : { id: 2, title: "No" }
                )
            }

            if (data.charger_details?.length > 0) {
                setShowMore(true)
                const hasExistingSavedState = data.charger_details.some((c: any) => c.plp_usage_id != null)
                const mappedRows: ChargerTableRow[] = data.charger_details.map((c: any, index: number) => ({
                    id: `charger-prefill-${index}-${Date.now()}`,
                    sku_code: c.sku_code || c.sku || "",
                    display_name: c.display_name || "",
                    cin: c.serial_cin || c.cin || "",
                    ocpp_id: c.ocpp_id || "",
                    remarks: c.remarks || "",
                    product_id: c.product_id,
                    project_id: c.project_id,
                    project_linked_product_id: c.project_product_id || c.project_linked_product_id,
                    plp_usage_id: c.plp_usage_id ?? null,
                    selected: hasExistingSavedState ? c.plp_usage_id != null : true,
                    status_label: (c.operational_parent_status_label || c.operational_status) ?? null,
                    sub_status_label: (c.operational_status_label || c.charger_sub_status) ?? null,
                    status_color: c.operational_status_color ?? null,
                    status_text_color: c.operational_status_text_color ?? null,
                }))
                const isSuppliedByStatiq = data.supplied_by_statiq == null || data.supplied_by_statiq === 1
                if (isSuppliedByStatiq) {
                    setStatiqChargerRows(mappedRows)
                } else {
                    setNonStatiqChargerRows(mappedRows)
                }
            }
        } catch (error) {
            console.error("Error fetching infra prefill data:", error)
        }
    }, [props.isEditMode, props.projectId, infraSOWOptions, surveyOptions, getProjectFormDetailsByStep, formik])

    useEffect(() => {
        fetchAndPrefillInfraData()
    }, [fetchAndPrefillInfraData])

    useEffect(() => {
        if (
            !hasAppliedDefaultScope.current &&
            infraRules.scope_default &&
            infraRules.scope_default.length > 0 &&
            infraSOWOptions.length > 0 &&
            formik.values[EInfraFields.INFRA_SCOPE_OF_WORK].length === 0
        ) {
            const defaultScopes = infraSOWOptions.filter(option =>
                infraRules.scope_default?.some((defaultName: string) =>
                    option.title.toLowerCase().includes(defaultName.toLowerCase())
                )
            )
            if (defaultScopes.length > 0) {
                hasAppliedDefaultScope.current = true
                formik.setFieldValue(EInfraFields.INFRA_SCOPE_OF_WORK, defaultScopes)
            }
        }
    }, [infraRules.scope_default, infraSOWOptions, formik.values[EInfraFields.INFRA_SCOPE_OF_WORK].length])

    function hasError(field: EInfraFields) {
        return Boolean(formik.submitCount > 0 && formik.errors[field])
    }

    const handleSelectAllSOW = (selectAll: boolean) => {
        if (selectAll) {
            formik.setFieldValue(EInfraFields.INFRA_SCOPE_OF_WORK, infraSOWOptions)
        } else {
            formik.setFieldValue(EInfraFields.INFRA_SCOPE_OF_WORK, [])
        }
    }

    const isAllSOWSelected = useMemo(() => {
        return (
            infraSOWOptions.length > 0 &&
            formik.values[EInfraFields.INFRA_SCOPE_OF_WORK].length === infraSOWOptions.length
        )
    }, [infraSOWOptions, formik.values])

    async function handleSubmit(values: InfraFormValues) {
        const isCurrentlyStatiqSupplied = values[EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]?.id === 1
        const activeChargerRows = isCurrentlyStatiqSupplied ? statiqChargerRows : nonStatiqChargerRows

        if (isCurrentlyStatiqSupplied && !activeChargerRows.some(row => row.selected)) {
            setShowMore(true)
            enqueueSnackbar("Please select at least one charger", { variant: "error" })
            return
        }

        const payload: IInfraDetailsPageData = {
            page_title: (props.pageData?.title as EProjectFormPages.INFRA_DETAILS) || EProjectFormPages.INFRA_DETAILS,
            project_id: project_id!,
            survey_id: values[EInfraFields.SURVEY_TICKET_ID]?.id || null,
            [EInfraFields.OCPP_URL]: values[EInfraFields.OCPP_URL],
            [EInfraFields.PORT_NUMBER]: Number(values[EInfraFields.PORT_NUMBER]) || 0,
            charger_details: (isCurrentlyStatiqSupplied
                ? activeChargerRows.filter(row => row.selected)
                : activeChargerRows
            ).map(row => ({
                ...(row.project_linked_product_id
                    ? { project_product_id: row.project_linked_product_id }
                    : {
                          product_id: row.product_id,
                      }),
                serial_cin: row.cin,
                ocpp_id: row.ocpp_id,
                sku_code: row.sku_code,
                remarks: row.remarks,
            })),
            [EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]:
                values[EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]?.id === 1 ? 1 : 0,
            [EInfraFields.INFRA_SCOPE_OF_WORK]: values[EInfraFields.INFRA_SCOPE_OF_WORK].map(sow => sow.id),
            [EInfraFields.IS_EVA_REQUIRED]: values[EInfraFields.IS_EVA_REQUIRED]?.title === "Yes" ? 1 : 0,
            [EInfraFields.REMARKS]: values[EInfraFields.REMARKS],
        }

        if (values[EInfraFields.IS_EVA_REQUIRED]?.title === "Yes") {
            payload[EInfraFields.EV_CONNECTION_OWNED_BY] = values[EInfraFields.EV_CONNECTION_OWNED_BY]?.id
        }

        try {
            await updateTicketPageData(payload).unwrap()

            // Submit pending file uploads
            await submitPendingFiles()

            dispatch(setInfraForm({ ...values, charger_details: activeChargerRows }))

            enqueueSnackbar("Infra details saved successfully", { variant: "success" })

            props.handleNextStep()
        } catch (error) {
            console.error("Error saving infra details:", error)
            enqueueSnackbar("Error saving infra details", { variant: "error" })
        }
    }

    useEffect(() => {
        if (formik.submitCount > 0 && formik.errors[EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]) {
            setShowMore(true)
        }
    }, [formik.errors, formik.submitCount])

    // Save charger details to Redux whenever they change
    useEffect(() => {
        if (infra_form && JSON.stringify(infra_form.charger_details) !== JSON.stringify(chargerTableRows)) {
            dispatch(setInfraForm({ ...infra_form, charger_details: chargerTableRows }))
        }
    }, [chargerTableRows, infra_form, dispatch])

    // Check if Commissioning scope is selected (for OCPP URL and Port Number visibility)
    const isCommissioningSelected = useMemo(() => {
        return formik.values[EInfraFields.INFRA_SCOPE_OF_WORK].some(scope =>
            scope.title?.toLowerCase().includes("commissioning")
        )
    }, [formik.values[EInfraFields.INFRA_SCOPE_OF_WORK]])

    // SB-8432: CPO chargers always talk to the same CSMS, so the URL and port are prefilled once the
    // commissioning scope brings those two fields on screen. Applied at most once per form and only to
    // an empty field, so neither the saved value nor anything the user types is fought over.
    const hasAppliedCpoInfraDefaults = useRef(false)

    useEffect(() => {
        if (!isCpoDeal || !isCommissioningSelected || hasAppliedCpoInfraDefaults.current) return

        hasAppliedCpoInfraDefaults.current = true

        if (!formik.values[EInfraFields.OCPP_URL]) {
            formik.setFieldValue(EInfraFields.OCPP_URL, CPO_DEFAULT_OCPP_URL)
        }
        if (!formik.values[EInfraFields.PORT_NUMBER]) {
            formik.setFieldValue(EInfraFields.PORT_NUMBER, CPO_DEFAULT_PORT_NUMBER)
        }
    }, [
        isCpoDeal,
        isCommissioningSelected,
        formik.values[EInfraFields.OCPP_URL],
        formik.values[EInfraFields.PORT_NUMBER],
    ])

    // Check if Installation or Electrical Work scope is selected (for EV Application visibility)
    const isInstallationOrElectricalSelected = useMemo(() => {
        return formik.values[EInfraFields.INFRA_SCOPE_OF_WORK].some(
            scope =>
                scope.title?.toLowerCase().includes("installation") || scope.title?.toLowerCase().includes("electrical")
        )
    }, [formik.values[EInfraFields.INFRA_SCOPE_OF_WORK]])

    // Check if EVA is required
    const isEvaRequired = formik.values[EInfraFields.IS_EVA_REQUIRED]?.title === "Yes"

    const handleChargerChange = useCallback(
        (index: number, field: keyof ChargerTableRow, value: string) => {
            const setter = isStatiqSupplied ? setStatiqChargerRows : setNonStatiqChargerRows
            setter(prev => {
                const updated = [...prev]
                updated[index] = { ...updated[index], [field]: value }
                return updated
            })
        },
        [isStatiqSupplied]
    )

    const handleAddCharger = useCallback(() => {
        const setter = isStatiqSupplied ? setStatiqChargerRows : setNonStatiqChargerRows
        setter(prev => [...prev, createEmptyChargerRow()])
    }, [isStatiqSupplied])

    const handleDeleteCharger = useCallback(
        (index: number) => {
            const setter = isStatiqSupplied ? setStatiqChargerRows : setNonStatiqChargerRows
            setter(prev => prev.filter((_, i) => i !== index))
        },
        [isStatiqSupplied]
    )

    const handleToggleCharger = useCallback((index: number) => {
        setStatiqChargerRows(prev => {
            const updated = [...prev]
            updated[index] = { ...updated[index], selected: !updated[index].selected }
            return updated
        })
    }, [])

    const handleSkuChange = useCallback(
        (index: number, product: Product | null) => {
            const setter = isStatiqSupplied ? setStatiqChargerRows : setNonStatiqChargerRows
            setter(prev => {
                const updated = [...prev]
                if (product) {
                    updated[index] = {
                        ...updated[index],
                        sku_code: product.sku_code,
                        display_name: product.display_name,
                        product_id: product.id,
                    }
                } else {
                    updated[index] = {
                        ...updated[index],
                        sku_code: "",
                        display_name: "",
                        product_id: undefined,
                    }
                }
                return updated
            })
        },
        [isStatiqSupplied]
    )

    const selectedProjectChargerOptions = useMemo(() => {
        if (!selectedProjectChargers) return []
        return selectedProjectChargers.map((charger: IProjectCharger, index: number) => ({
            id: index,
            title: charger.sku
                ? `${charger.sku} ${charger.cin ? `- ${charger.cin}` : ""} ${charger.operational_status_label ? `- ${charger.operational_status_label}` : ""}`
                : `Charger ${index + 1}`,
            charger,
            disabled: isChargerDisabled(charger.operational_status_id || 0),
        }))
    }, [selectedProjectChargers])

    // Auto-select chargers that are already in the charger table
    useEffect(() => {
        const selectedProjectId = formik.values[EInfraFields.SELECT_PROJECT]?.id

        // If no project is selected, clear the chargers dropdown and reset ref
        if (!selectedProjectId) {
            hasPreselectedChargers.current = null
            formik.setFieldValue(EInfraFields.SELECT_CHARGERS_TO_ADD, [])
            return
        }

        if (!selectedProjectChargers || selectedProjectChargers.length === 0) {
            return
        }

        // Only run preselection once per project selection
        if (hasPreselectedChargers.current === selectedProjectId) {
            return
        }

        hasPreselectedChargers.current = selectedProjectId as number

        // Find chargers that are already in the table from the selected project
        const alreadyAddedChargers = statiqChargerRows.filter(row => row.project_id === Number(selectedProjectId))

        // Map the already added chargers to the dropdown options
        const preselectedOptions = selectedProjectChargerOptions.filter(option => {
            const charger = option.charger
            if (!charger) return false

            return alreadyAddedChargers.some(addedCharger => {
                if (addedCharger.project_linked_product_id && charger.project_linked_product_id) {
                    return addedCharger.project_linked_product_id === charger.project_linked_product_id
                }
            })
        })

        // Set the preselected options (empty array if none found)
        formik.setFieldValue(EInfraFields.SELECT_CHARGERS_TO_ADD, preselectedOptions)
    }, [selectedProjectChargers, selectedProjectChargerOptions, formik.values[EInfraFields.SELECT_PROJECT]])

    const handleProjectChange = useCallback(
        (value: { id: string | number; title: string } | { id: string | number; title: string }[] | null) => {
            const selectedProject = Array.isArray(value) ? value[0] : value
            formik.setFieldValue(EInfraFields.SELECT_PROJECT, selectedProject)
            if (selectedProject?.id) {
                fetchSelectedProjectChargers(Number(selectedProject.id))
            } else {
                formik.setFieldValue(EInfraFields.SELECT_CHARGERS_TO_ADD, [])
            }
        },
        [formik, fetchSelectedProjectChargers]
    )

    const handleAddSelectedChargers = useCallback(() => {
        const selectedChargers = formik.values[EInfraFields.SELECT_CHARGERS_TO_ADD] as (DropdownObj & {
            charger?: IProjectCharger
        })[]
        if (!selectedChargers || selectedChargers.length === 0) return

        // Filter out chargers that are already in the table
        const chargersToAdd = selectedChargers.filter(selected => {
            const charger = selected.charger
            if (!charger) return false

            // Check if this charger is already in the table
            const alreadyExists = statiqChargerRows.some(row => {
                if (row.project_linked_product_id && charger.project_linked_product_id) {
                    return row.project_linked_product_id === charger.project_linked_product_id
                }
                return false
            })

            return !alreadyExists
        })

        if (chargersToAdd.length === 0) {
            enqueueSnackbar("Selected chargers are already added", { variant: "info" })
            return
        }

        const newRows: ChargerTableRow[] = chargersToAdd.map((selected, index) => {
            const charger = selected.charger!
            return {
                id: `charger-added-${Date.now()}-${index}`,
                sku_code: charger.sku || "",
                display_name: charger.display_name || "",
                cin: charger.cin || "",
                ocpp_id: charger.ocpp_id || "",
                remarks: charger.remarks || "",
                project_id: charger.project_id,
                project_linked_product_id: charger.project_linked_product_id,
                plp_usage_id: charger.plp_usage_id ?? null,
                selected: true,
                status_label: charger.operational_parent_status_label ?? null,
                sub_status_label: charger.operational_status_label ?? null,
                status_color: charger.operational_status_color ?? null,
                status_text_color: charger.operational_status_text_color ?? null,
            }
        })

        setStatiqChargerRows(prev => [...prev, ...newRows])
    }, [formik, statiqChargerRows])

    const handleAddChargersFromChange = useCallback(
        (value: DropdownObj) => {
            setAddChargersFrom(value)
            formik.setFieldValue(EInfraFields.SELECT_PROJECT, null)
            formik.setFieldValue(EInfraFields.SELECT_CHARGERS_TO_ADD, [])
            if (value.title === "Site" && account_n_sites_form_values?.destinationSite?.id) {
                fetchSiteChargers({
                    site_id: account_n_sites_form_values.destinationSite.id,
                    ticket_id: project_id ?? undefined,
                })
            }
        },
        [account_n_sites_form_values, project_id, fetchSiteChargers, formik]
    )

    useEffect(() => {
        formik.setFieldValue(EInfraFields.SELECT_PROJECT, null)
        formik.setFieldValue(EInfraFields.SELECT_CHARGERS_TO_ADD, [])
    }, [])

    return {
        states: {
            infraSOWOptions,
            surveyOptions,
            yesNoOptions,
            statiqSupplyOptions,
            showMore,
            evConnectionOwnedByOptions,
            formik,
            infraSowsIsFetching,
            chargerTableRows,
            chargerProducts: chargerProducts || [],
            isStatiqSupplied,
            hasChargerData,
            isLoading: isSubmittingFinalUploads || isUpdatingPageData,
            project_id: project_id ?? undefined,
            FILE_TYPES,
            isAllSOWSelected,
            isCommissioningSelected,
            isInstallationOrElectricalSelected,
            isEvaRequired,
            dispatchedProjectsTrigger: getDispatchedChargerProjects,
            selectedProjectChargerOptions,
            isSelectedProjectChargersFetching,
            existingInfraDocuments,
            showAddChargersFromProjects,
            addChargersFromOptions,
            addChargersFrom,
            isSiteSelected,
            destinationSiteOption,
            selectedSiteChargerOptions,
            isSiteChargersFetching,
        },
        functions: {
            handleSubmit,
            setShowMore,
            hasError,
            handleChargerChange,
            handleAddCharger,
            handleDeleteCharger,
            handleSkuChange,
            handleFileUpload,
            handleSelectAllSOW,
            handleProjectChange,
            handleAddSelectedChargers,
            handleToggleCharger,
            handleAddChargersFromChange,
        },
    }
}

export default useInfra
